import { AlertTriangleIcon, Loader2Icon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { NoteVault } from "@/lib/types";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useDeleteNoteVault } from "@/hooks/queries/useDeleteNoteVault";

export function DeleteVaultDialog({
  open,
  onOpenChange,
  vault,
  onDeleteVault,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vault: NoteVault | null;
  onDeleteVault: () => void;
}) {
  const [confirmationString, setConfirmationString] = useState("");

  const deleteVault = useDeleteNoteVault();

  const handleDelete = () => {
    if (confirmationString === vault?.name) {
      onDeleteVault();
    } else {
      toast.error("Incorrect confirmation string");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangleIcon className="h-5 w-5 text-destructive" />
            <span>Delete Vault</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Alert className="bg-destructive/50 text-foreground">
            <AlertDescription>
              This action cannot be undone. All notebooks and notes in this
              vault will be permanently deleted.
            </AlertDescription>
          </Alert>

          <p className="text-muted-foreground">
            Please type the name of the vault to confirm deletion (
            <span className="font-medium text-foreground">"{vault?.name}"</span>
            ):
          </p>

          <Input
            value={confirmationString}
            onChange={e => setConfirmationString(e.target.value)}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteVault.isPending}
          >
            {deleteVault.isPending ? (
              <>
                <Loader2Icon className="h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Vault"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
